import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from '@michelin/central-provider';
import {
  Container,
  LoadingBounce,
  Panel,
  PreventTransitionPrompt,
  useDialogs,
} from '@michelin/acid-components';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { PageTitle } from 'Utils/CanvasPageTitle';
import { useForm } from 'react-hook-form';
import { DataGrid } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import { useSnackbar } from 'notistack';
import { sleep } from 'Utils';
import { SelectLocationsModal } from '../LocationSelector/SelLocationsModal';
import { ActionButtons } from '../Components/Actions';
import {
  useGetTechServAreas,
  useSaveTechServiceAreas,
} from '../../../hooks/useTechServiceAreas';
import {
  ActionsCell,
  RenderCity,
  RenderSecondary,
  RenderShipToInfo,
} from '../Components/TableColumns';
import { SearchBar } from '../Components/SearchBar';
import {
  mapDataToBeSaved,
  mapOnDeleteSTServArea,
  serviceAreasDataMap,
} from '../utils';
import { PrimTechServiceAreaLocation } from '../types';
import { OverlayLoader } from '../Components/OverlayLoader';

interface FormData {
  areas: PrimTechServiceAreaLocation[];
  originalPrimaries: string[];
}

const TABLE_HEIGHT_WITH_SEARCH = `calc(100vh - ${
  runningOnCanvas() ? '100px' : '199px'
} - 56px)`;
// const TABLE_HEIGHT_WITHOUT_SEARCH = `calc(100vh - ${
//   runningOnCanvas() ? '100px' : '199px'
// })`;
export function TechnicianServiceAreaEdit(props: { action: 'view' | 'edit' }) {
  const { stNumber, btNumber } = useParams<{
    stNumber: string;
    btNumber: string;
  }>();
  const { action } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const permissions = usePermissionsSPP();
  const theme = useTheme();
  const cellView = useMediaQuery(theme.breakpoints.down(800));
  const { enqueueSnackbar } = useSnackbar();
  const { confirmDialog } = useDialogs();
  const [modalLocInfo, setModalLocInfo] = useState({
    btNumber,
    stNumber: '',
    isPrimary: false,
    isSecondary: false,
    secondaryLocations: [] as string[],
    openModal: false,
    primaryLocationInfo: {} as PrimTechServiceAreaLocation,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [filterEmpty, setFilterEmpty] = useState(false);
  const [saving, setSaving] = useState<boolean>(false);
  const mutateTechServiceAreas = useSaveTechServiceAreas();
  const getTechServAreasData = useGetTechServAreas({ btNumber, stNumber });
  const {
    reset, getValues, formState, setValue, watch,
  } = useForm<FormData>({
    defaultValues: { areas: [], originalPrimaries: [] },
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const watchAreas = watch('areas');
  const isDirty = Object.keys(formState.dirtyFields).length > 0;

  useEffect(() => {
    if (
      getTechServAreasData.isLoading
      || !getTechServAreasData.data
      || getTechServAreasData.isFetching
    ) return;
    const { data } = getTechServAreasData;
    const newData = serviceAreasDataMap(data);
    reset({ areas: newData.data, originalPrimaries: newData.originales });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getTechServAreasData.isLoading,
    getTechServAreasData.data,
    getTechServAreasData.isFetching,
  ]);

  const filteredAreas = useMemo(() => {
    if (!searchValue && !filterEmpty) return watchAreas;
    if (!watchAreas || watchAreas.length === 0) return [];
    if (!searchValue && filterEmpty) {
      return watchAreas.filter(
        (area) => !area.secondary_st_locations
          || area.secondary_st_locations.length === 0,
      );
    }
    const lowerSearch = searchValue.toLowerCase();
    const searchFilterData = watchAreas.filter(
      (area) => area.ship_to_number?.toLowerCase().includes(lowerSearch)
        || area.customer_number?.toLowerCase().includes(lowerSearch),
    );
    if (filterEmpty) {
      return searchFilterData.filter(
        (area) => !area.secondary_st_locations
          || area.secondary_st_locations.length === 0,
      );
    }
    return searchFilterData;
  }, [watchAreas, searchValue, filterEmpty]);

  useEffect(() => {
    setTotalCount(filteredAreas.length);
  }, [filteredAreas]);
  useEffect(() => {
    setSaving(false);
  }, [props.action]);

  useEffect(() => {
    if (action === 'view' && filterEmpty) setFilterEmpty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  if (permissions.isLoading) {
    return <LoadingBounce style={{ height: '70vh' }} />;
  }
  if (!permissions.allowsAction('technician_service_area.read')) {
    history.push(`/${btNumber}`);
  }
  const updatePermission = permissions.allowsAction(
    'technician_service_area.update',
  );
  if (action === 'edit' && !updatePermission) {
    history.push(`/${btNumber}/technicianServiceArea/view`);
  }

  const RenderSelectLocationsModal = () => {
    if (!modalLocInfo.openModal) return <></>;
    if (!permissions || !permissions.location) {
      setModalLocInfo({
        ...modalLocInfo,
        openModal: false,
        primaryLocationInfo: {} as PrimTechServiceAreaLocation,
      });
      return <></>;
    }
    return (
      <SelectLocationsModal
        onClose={() => {
          setModalLocInfo({
            ...modalLocInfo,
            openModal: false,
            secondaryLocations: [],
            isSecondary: false,
            isPrimary: false,
            stNumber: '',
            primaryLocationInfo: {} as PrimTechServiceAreaLocation,
          });
        }}
        onUpdate={(data) => {
          if (data.isPrimary) {
            const currentAreas = getValues().areas || [];
            const existingPrimary = currentAreas.find(
              (area) => area.ship_to_number === data.primaryLocationInfo.ship_to_number,
            );
            if (existingPrimary) {
              enqueueSnackbar(t('Primary ST Location already exists.'), {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
              });
              return;
            }
            const newAreas: PrimTechServiceAreaLocation[] = [
              {
                ...data.primaryLocationInfo,
                secondary_st_locations: [],
                ship_to_number: data.primaryLocationInfo.ship_to_number || '',
              },
              ...currentAreas,
            ];
            setValue('areas', newAreas, { shouldDirty: true });
            setModalLocInfo({
              ...modalLocInfo,
              openModal: false,
              secondaryLocations: [],
              isSecondary: false,
              isPrimary: false,
              stNumber: '',
              primaryLocationInfo: {} as PrimTechServiceAreaLocation,
            });
          } else {
            const prymToEdit = data.st;
            const uniqueSecondaryLocations = Array.from(
              new Set(data.secondaryLocations),
            );
            const currentAreas = getValues().areas || [];
            const newAreas = currentAreas.map((area) => {
              if (area.ship_to_number === prymToEdit) {
                return {
                  ...area,
                  secondary_st_locations: uniqueSecondaryLocations.sort(),
                };
              }
              return area;
            });
            setValue('areas', newAreas, { shouldDirty: true });
            setModalLocInfo({
              ...modalLocInfo,
              openModal: false,
              secondaryLocations: [],
              isSecondary: false,
              isPrimary: false,
              stNumber: '',
              primaryLocationInfo: {} as PrimTechServiceAreaLocation,
            });
          }
        }}
        bt={modalLocInfo.btNumber}
        primarySt={modalLocInfo.stNumber}
        isPrimary={modalLocInfo.isPrimary}
        primaryLocations={watchAreas.map((area) => area.ship_to_number)}
        secondaryLocations={modalLocInfo.secondaryLocations}
        primaryInfo={modalLocInfo.primaryLocationInfo}
      />
    );
  };

  const onDeleteSecondary = (primSt: string, secSt: string) => {
    const currentAreas = getValues().areas || [];
    const newAreas = currentAreas.map((area) => {
      if (area.ship_to_number === primSt) {
        return {
          ...area,
          secondary_st_locations: area.secondary_st_locations?.filter(
            (st) => st !== secSt,
          ),
        };
      }
      return area;
    });
    setValue('areas', newAreas, { shouldDirty: true });
  };

  const onDeleteSTServArea = async () => {
    const dataToBeSaved = mapOnDeleteSTServArea(stNumber || '');
    try {
      await mutateTechServiceAreas.mutateAsync({
        btNumber,
        data: dataToBeSaved,
      });
      await sleep(1500);
      history.push(`/${btNumber}`);
      setSaving(false);
      enqueueSnackbar(t('Technician Service Areas Deleted'), {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } catch (error) {
      enqueueSnackbar(t('Error Deleting Technician Service Areas.'), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
    setSaving(false);
  };
  const handleDeleteClick = async (st: string) => {
    if (!stNumber) {
      const currentAreas = getValues().areas || [];
      const newAreas = currentAreas.filter(
        (area) => area.ship_to_number !== st,
      );
      setValue('areas', newAreas, { shouldDirty: true });
    } else {
      await confirmDialog(
        t(
          'Are you sure you want to delete this technician service area?\n This action cannot be undone.',
        ).split('\n'),
        t('Delete Technician Service Area'),
        t('Continue'),
        t('Cancel'),
        () => {
          setSaving(true);
          onDeleteSTServArea();
        },
        () => {},
      );
    }
  };
  const handleEditClick = () => {
    if (!stNumber) {
      history.push(`/${btNumber}/technicianServiceArea/edit`);
    } else {
      history.push(`/${btNumber}/technicianServiceArea/${stNumber}/edit`);
    }
  };
  const handleCancelClick = () => {
    if (!stNumber) {
      history.push(`/${btNumber}/technicianServiceArea/view`);
    } else {
      history.push(`/${btNumber}/technicianServiceArea/${stNumber}/view`);
    }
  };

  const onSave = async (data: PrimTechServiceAreaLocation[]) => {
    const dataToBeSaved = mapDataToBeSaved(
      data,
      getValues().originalPrimaries || [],
    );
    try {
      await mutateTechServiceAreas.mutateAsync({
        btNumber,
        data: dataToBeSaved,
      });
      const newData = serviceAreasDataMap(data);
      reset({ areas: newData.data, originalPrimaries: newData.originales });
      if (
        stNumber
        && dataToBeSaved.ship_to_list[0].secondary_st_locations.length === 0
      ) {
        await sleep(1500);
        history.push(`/${btNumber}`);
      } else {
        history.push('view');
      }
      enqueueSnackbar(t('Technician Service Areas Saved.'), {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } catch (error) {
      enqueueSnackbar(t('Error Saving Technician Service Areas.'), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
    setSaving(false);
  };
  const handleSaveClick = async () => {
    setSaving(true);
    const dataToSave = getValues().areas || [];
    const hasEmptySecondary = dataToSave.some(
      (area) => !area.secondary_st_locations
        || (area.secondary_st_locations
          && area.secondary_st_locations.length === 0),
    );
    if (hasEmptySecondary) {
      await confirmDialog(
        !stNumber
          ? t(
            'Some technician service areas have no secondary ST locations selected. If the secondary ST locations are empty, the service area will be removed. Do you want to continue?',
          )
          : t(
            'This technician service area has no secondary ST locations selected. If the secondary ST locations are empty, the service area will be removed. Do you want to continue?',
          ),
        t('Empty Secondary ST Locations'),
        t('Continue'),
        t('Cancel'),
        () => {
          const filteredData = dataToSave.filter(
            (area) => area.secondary_st_locations
              && area.secondary_st_locations.length > 0,
          );
          onSave(filteredData);
        },
        () => {
          setSaving(false);
        },
      );
    } else {
      onSave(dataToSave);
    }
  };
  return (
    <>
      <PageTitle
        title={
          stNumber
            ? t('Technician Service Area')
            : t('Technician Service Areas')
        }
        goHomeBtn
      />
      <Container>
        <Panel spacing={0}>
          <RenderSelectLocationsModal />
          <SearchBar
            value={searchValue}
            onChange={setSearchValue}
            resultsCount={totalCount}
            total={watchAreas.length}
            cellView={cellView}
            placeholder={t('Search by Primary ST Location')}
            searchWidth={250}
            filterEmptyEnabled={action === 'edit'}
            filterEmpty={filterEmpty}
            onToggleFilterEmpty={(checked) => {
              setFilterEmpty(checked);
            }}
            disabled={!!stNumber}
          />
          <DataGrid
            style={{
              height: TABLE_HEIGHT_WITH_SEARCH,
            }}
            noDataText={t('No technician service area found...')}
            scrolling={{ mode: 'virtual', rowRenderingMode: 'standard' }}
            className="freespaced-table"
            dataSource={filteredAreas}
            allowColumnReordering
            allowColumnResizing
            wordWrapEnabled
            columnAutoWidth
            repaintChangesOnly
          >
            <Column
              caption={t('Primary ST Locations')}
              cellRender={({ row }) => <RenderShipToInfo loc={row.data} />}
              allowSearch
              allowSorting={false}
              dataField="ship_to_number"
              width={280}
            />
            <Column
              caption={t('Location Address')}
              cellRender={({ row }) => <RenderCity loc={row.data} />}
              dataField="address"
              allowFiltering={false}
              allowSorting={false}
              width={280}
            />
            <Column
              caption={t('Secondary ST Locations')}
              cellRender={({ row }) => (
                <RenderSecondary
                  data={row.data}
                  onDeleteClick={(primSt: string, secSt: string) => onDeleteSecondary(primSt, secSt)}
                  deleteDisabled={action === 'view'}
                />
              )}
              dataField="secondaryLocations"
              allowFiltering={false}
              allowSorting={false}
              minWidth={210}
            />
            {updatePermission && (
              <Column
                allowSearch={false}
                allowSorting={false}
                width={170}
                cellRender={(row) => (
                  <ActionsCell
                    row={row.data}
                    action={action}
                    onEditClick={(st, locations, primaryInfo) => {
                      setModalLocInfo({
                        ...modalLocInfo,
                        openModal: true,
                        stNumber: st,
                        isSecondary: true,
                        secondaryLocations: locations,
                        primaryLocationInfo: primaryInfo,
                      });
                    }}
                    onDeleteClick={(st) => handleDeleteClick(st)}
                    editDisabled={action !== 'edit' || saving}
                    deleteDisabled={action !== 'edit' || saving}
                  />
                )}
              />
            )}
          </DataGrid>
          <OverlayLoader
            show={
              getTechServAreasData.isLoading
              || getTechServAreasData.isFetching
              || saving
            }
          />
          <PreventTransitionPrompt
            when={isDirty && !saving && action !== 'view'}
            handleDiscard={() => {
              reset();
            }}
          />
          <ActionButtons
            updatePermission={updatePermission}
            action={action}
            isDirty={isDirty}
            addButton={!stNumber}
            onSaveClick={() => handleSaveClick()}
            onCancelClick={() => handleCancelClick()}
            onEditClick={() => handleEditClick()}
            onAddClick={() => {
              setModalLocInfo({
                ...modalLocInfo,
                isPrimary: true,
                stNumber: '',
                openModal: true,
              });
            }}
            saving={saving}
            editDisabled={
              !!stNumber && action === 'view' && filteredAreas.length === 0
            }
          />
        </Panel>
      </Container>
    </>
  );
}
